export const PRICING_UPDATED_EVENT = 'vbot-pricing-updated';

/** Annual plans store per-month rate; checkout charges 12 months. */
export const ANNUAL_BILLING_MONTHS = 12;

/** Matches BE pricingPlanModel defaults when API is unavailable */
export const DEFAULT_FALLBACK_AMOUNTS = {
  starter: { monthly: 79, annually: 67 },
  professional: { monthly: 149, annually: 127 },
  // elite: { monthly: 229, annually: 195 },
};

/** Default checkout/trial tier (7-day trial on Professional per pricing spec) */
export const DEFAULT_PLAN_KEY = 'professional';

export function notifyPricingUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PRICING_UPDATED_EVENT));
  }
}

export function formatUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  const n = Number(amount);
  if (Number.isInteger(n)) return `$${n}`;
  const fixed = n.toFixed(2);
  return `$${fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed}`;
}

export function formatPerMonth(amount, { suffix = '/mo' } = {}) {
  const label = formatUsd(amount);
  return label ? `${label}${suffix}` : null;
}

function stripPeriodSuffix(displayName, planKey) {
  return (
    displayName?.replace(/\s*\([^)]*\)\s*$/, "").trim() ||
    String(planKey || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** Group active API plans into display cards for a billing period */
export function buildPlanCardsFromApi(plans, billingPeriod) {
  if (!Array.isArray(plans) || !plans.length) return [];

  const grouped = {};
  for (const row of plans) {
    if (row.is_active === false || row.is_active === 0) continue;
    if (!grouped[row.plan_key]) {
      grouped[row.plan_key] = { planKey: row.plan_key, periods: {} };
    }
    grouped[row.plan_key].periods[row.billing_period] = row;
  }

  return Object.values(grouped)
    .map((g) => {
      const current =
        g.periods[billingPeriod] || Object.values(g.periods)[0];
      if (!current) return null;
      const amount = parseFloat(current.amount);
      if (!Number.isFinite(amount) || amount <= 0) return null;
      return {
        planKey: g.planKey,
        name: stripPeriodSuffix(current.display_name, g.planKey),
        description: current.description || "",
        amount,
        perSeat: Boolean(current.per_seat),
        priceSuffix: current.per_seat ? "/clinician" : "",
        sortOrder: current.sort_order ?? 0,
        raw: current,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Prefer API planGroups (structured DB response), else flat plans rows.
 * Falls back to DEFAULT_FALLBACK_AMOUNTS when the API has no active paid plans.
 */
export function resolveDisplayPlanCards({
  plans,
  planGroups,
  billingPeriod,
  getAmount,
}) {
  if (Array.isArray(planGroups) && planGroups.length) {
    const fromGroups = planGroups
      .map((group) => {
        const period =
          group.periods?.[billingPeriod] ||
          group.periods?.monthly ||
          group.periods?.annually ||
          Object.values(group.periods || {})[0];
        if (!period) return null;
        const amount =
          getAmount?.(group.planKey, billingPeriod, 1) ??
          parseFloat(period.amount);
        if (!Number.isFinite(amount) || amount <= 0) return null;
        return {
          planKey: group.planKey,
          name:
            group.displayName ||
            stripPeriodSuffix(period.displayName, group.planKey),
          description: period.description || group.description || "",
          amount,
          perSeat: Boolean(group.perSeat ?? period.perSeat),
          priceSuffix: group.perSeat || period.perSeat ? "/clinician" : "",
          sortOrder: group.sortOrder ?? period.sortOrder ?? 0,
          raw: period,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (fromGroups.length) return fromGroups;
  }

  const fromPlans = buildPlanCardsFromApi(plans, billingPeriod);
  if (fromPlans.length) return fromPlans;

  return Object.entries(DEFAULT_FALLBACK_AMOUNTS).map(
    ([planKey, amounts], index) => ({
      planKey,
      name: stripPeriodSuffix(null, planKey),
      description: "",
      amount: amounts[billingPeriod] ?? amounts.monthly,
      perSeat: false,
      priceSuffix: "",
      sortOrder: index + 1,
      raw: null,
    })
  );
}

/** Split a DB description into feature bullets when no feature list exists. */
export function featuresFromDescription(description) {
  if (!description?.trim()) return [];
  const parts = description
    .split(/\s*[—–|]\s*|\s*;\s*|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [description.trim()];
}

/** Per-month plan amount → total charged at checkout. */
export function toCheckoutAmount(perMonthAmount, billingPeriod) {
  const unit = Number(perMonthAmount);
  if (Number.isNaN(unit)) return null;
  if (billingPeriod === 'annually') {
    return parseFloat((unit * ANNUAL_BILLING_MONTHS).toFixed(2));
  }
  return unit;
}

export function computeAnnualSavingsPercent(monthlyAmount, annualAmount) {
  const monthly = Number(monthlyAmount);
  const annual = Number(annualAmount);
  if (!monthly || !annual || monthly <= 0) return 0;
  const pct = Math.round((1 - annual / monthly) * 100);
  return pct > 0 ? pct : 0;
}
